import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { messagingService } from '../../../lib/services';
import { formatDistanceToNow } from 'date-fns';

const StaffConversationView = ({ thread, onSendMessage, currentUserId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (thread?.id) {
      loadMessages();
      setupRealtimeSubscription();
    }

    return () => {
      if (subscription) {
        subscription?.unsubscribe();
      }
    };
  }, [thread?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async () => {
    setLoading(true);
    const { data, error } = await messagingService?.getThreadMessages(thread?.id);
    if (!error && data) {
      setMessages(data);
    }
    setLoading(false);
  };

  const setupRealtimeSubscription = () => {
    const sub = messagingService?.subscribeToThread(thread?.id, (newMessage) => {
      setMessages(prev => [...prev, newMessage]);
    });
    setSubscription(sub);
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!newMessage?.trim() || sending) return;

    setSending(true);
    const { error } = await onSendMessage(newMessage);
    if (!error) {
      setNewMessage('');
    }
    setSending(false);
  };

  const getOtherParticipants = () => {
    return thread?.participants
      ?.filter(p => p?.user_id !== currentUserId)
      ?.map(p => p?.user_profile?.full_name)
      ?.join(', ') || 'Unknown';
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      staff: 'bg-green-500/10 text-green-600',
      manager: 'bg-orange-500/10 text-orange-600',
      owner: 'bg-red-500/10 text-red-600',
      sysadmin: 'bg-purple-500/10 text-purple-600'
    };
    return colors?.[role] || 'bg-muted text-muted-foreground';
  };

  const isBroadcast = thread?.subject?.startsWith('[BROADCAST]');

  return (
    <div className="bg-card rounded-xl shadow-md border border-border flex flex-col h-[600px]">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isBroadcast ? 'bg-accent/10' : 'bg-primary/10'
          }`}>
            <Icon 
              name={isBroadcast ? 'Radio' : 'Users'} 
              size={20} 
              color={isBroadcast ? 'var(--color-accent)' : 'var(--color-primary)'} 
            />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-foreground">
              {isBroadcast ? 'Team Broadcast' : getOtherParticipants()}
            </h2>
            <p className="text-xs text-muted-foreground">
              {thread?.subject?.replace('[BROADCAST] ', '')}
            </p>
          </div>
          {isBroadcast && (
            <span className="px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-medium">
              Broadcast Message
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : messages?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Icon name="MessageSquare" size={48} color="var(--color-muted-foreground)" className="mb-3" />
            <p className="text-sm text-muted-foreground">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages?.map((message) => {
            const isOwn = message?.sender_id === currentUserId;
            return (
              <div
                key={message?.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[70%] ${isOwn ? 'order-2' : 'order-1'}`}>
                  {!isOwn && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-foreground">
                        {message?.sender?.full_name}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(message?.sender?.role)}`}>
                        {message?.sender?.role}
                      </span>
                    </div>
                  )}
                  <div
                    className={`
                      rounded-lg p-3
                      ${isOwn
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                      }
                    `}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">{message?.content}</p>
                  </div>
                  <p className={`text-xs text-muted-foreground mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                    {formatDistanceToNow(new Date(message?.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="p-4 border-t border-border">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e?.target?.value)}
              disabled={sending}
            />
          </div>
          <Button
            type="submit"
            disabled={!newMessage?.trim() || sending}
            iconName="Send"
            iconPosition="right"
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StaffConversationView;